/**
 * Lite rendering for weak TV hardware. A first-generation Chromecast runs the
 * same table a current one does, but cannot afford the big blurred glows,
 * the spinning jackpot rays or a screen of raining coins. Lite mode keeps
 * every deal, spin, toss and payout moving and only trims those paint-heavy
 * extras, via `html[data-lite]` rules beside each effect's own CSS.
 *
 * It switches itself on for a cast device with two cores or less or 1 GB of
 * memory or less. `?lite=1` / `?lite=0` on any page pins it on or off for
 * that browser, and `?lite=auto` hands the choice back to detection.
 */
export type LitePreference = 'on' | 'off' | null;

export interface LiteInput {
  /** `location.search` — carries a one-off `lite` override. */
  search: string;
  userAgent: string;
  /** `navigator.hardwareConcurrency`; undefined where the browser hides it. */
  cores?: number;
  /** `navigator.deviceMemory` in GB; Chromium only. */
  memoryGb?: number;
  /** What an earlier `?lite=` pinned in this browser. */
  stored: LitePreference;
}

export interface LiteDecision {
  lite: boolean;
  /** What to remember for the next load; undefined leaves storage alone. */
  store?: LitePreference;
}

/** The Cast receiver's user agent names its firmware as `CrKey/x.y`. */
export const isCastDevice = (userAgent: string): boolean => /\bCrKey\//.test(userAgent);

/** A cast device that the paint-heavy effects are known to outrun. */
export const isWeakCastDevice = (input: Pick<LiteInput, 'userAgent' | 'cores' | 'memoryGb'>): boolean =>
  isCastDevice(input.userAgent) &&
  ((input.cores !== undefined && input.cores <= 2) ||
    (input.memoryGb !== undefined && input.memoryGb <= 1));

export function liteMode(input: LiteInput): LiteDecision {
  const asked = new URLSearchParams(input.search).get('lite');
  if (asked === '1' || asked === 'on') return { lite: true, store: 'on' };
  if (asked === '0' || asked === 'off') return { lite: false, store: 'off' };
  const detected = isWeakCastDevice(input);
  if (asked === 'auto') return { lite: detected, store: null };
  if (input.stored === 'on') return { lite: true };
  if (input.stored === 'off') return { lite: false };
  return { lite: detected };
}

const STORAGE_KEY = 'tvcasino.lite';

/** Reads the browser, decides, and marks `<html data-lite>` before first paint. */
export function applyLiteMode(): boolean {
  if (typeof window === 'undefined') return false;
  let stored: LitePreference = null;
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    stored = raw === 'on' || raw === 'off' ? raw : null;
  } catch {
    /* storage blocked: detection alone decides */
  }
  const nav = navigator as Navigator & { deviceMemory?: number };
  const decision = liteMode({
    search: window.location.search,
    userAgent: nav.userAgent,
    cores: nav.hardwareConcurrency || undefined,
    memoryGb: nav.deviceMemory,
    stored,
  });
  if (decision.store !== undefined) {
    try {
      if (decision.store === null) localStorage.removeItem(STORAGE_KEY);
      else localStorage.setItem(STORAGE_KEY, decision.store);
    } catch {
      /* ignore */
    }
  }
  if (decision.lite) document.documentElement.setAttribute('data-lite', '');
  else document.documentElement.removeAttribute('data-lite');
  return decision.lite;
}
